import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE } from '../utils/api';

function AdminPlanRequestManager() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const token = localStorage.getItem('token');

  const loadRequests = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_BASE}/admin/plan-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(Array.isArray(response.data) ? response.data : []);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to load plan requests.');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const approve = async (request) => {
    if (!window.confirm(`Approve ${request.email} for ${request.requestedPlanName}?`)) return;
    try {
      await axios.post(`${API_BASE}/admin/plan-requests/${encodeURIComponent(request.userId)}/approve`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadRequests();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to approve the plan request.');
    }
  };

  const reject = async (request) => {
    const note = window.prompt(`Reason for rejecting ${request.email}?`, '') ?? null;
    if (note === null) return;
    try {
      await axios.post(`${API_BASE}/admin/plan-requests/${encodeURIComponent(request.userId)}/reject`, { note }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await loadRequests();
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'Unable to reject the plan request.');
    }
  };

  if (loading) return <div style={styles.message}>Loading plan requests...</div>;

  return (
    <div>
      <p style={styles.intro}>Approve free pre-launch access to Business Starter and Enterprise Pro. Existing members of those plans remain active automatically.</p>
      {error && <div style={styles.error}>{error}</div>}
      {requests.length === 0 ? (
        <div style={styles.message}>No plan requests are waiting for approval.</div>
      ) : (
        <div style={styles.grid}>
          {requests.map(request => (
            <article key={request.userId} style={styles.card}>
              <h3 style={styles.heading}>{request.fullName || request.email}</h3>
              <p><strong>Email:</strong> {request.email}</p>
              <p><strong>Current plan:</strong> {request.currentPlanName}</p>
              <p><strong>Requested plan:</strong> {request.requestedPlanName}</p>
              <p><strong>Requested:</strong> {request.requestedAt ? new Date(request.requestedAt).toLocaleString() : 'Unknown'}</p>
              <div style={styles.business}>
                <h4 style={styles.subheading}>Business information</h4>
                <p><strong>Company:</strong> {request.businessProfile?.companyName || 'Not provided'}</p>
                <p><strong>Business:</strong> {request.businessProfile?.businessName || 'Not provided'}</p>
                <p><strong>Business ID:</strong> {request.businessProfile?.businessId || 'Not provided'}</p>
                <p><strong>Website:</strong> {request.businessProfile?.websiteUrl || 'Not provided'}</p>
                <p><strong>Industry:</strong> {request.businessProfile?.industry || 'Not provided'}</p>
                <p><strong>Contact:</strong> {request.businessProfile?.contactEmail || 'Not provided'}</p>
              </div>
              <div style={styles.actions}>
                <button type="button" style={styles.approve} onClick={() => approve(request)}>Approve</button>
                <button type="button" style={styles.reject} onClick={() => reject(request)}>Reject</button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

const styles = {
  intro: { color: '#475569', lineHeight: 1.5, marginTop: 0 },
  grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '16px' },
  card: { border: '1px solid #e2e8f0', borderRadius: '10px', padding: '18px', background: '#fff' },
  heading: { marginTop: 0, color: '#0f172a' },
  subheading: { margin: '0 0 8px', color: '#334155' },
  business: { marginTop: '14px', paddingTop: '14px', borderTop: '1px solid #e2e8f0' },
  actions: { display: 'flex', gap: '10px', marginTop: '16px' },
  approve: { border: 0, borderRadius: '6px', padding: '10px 16px', background: '#15803d', color: '#fff', cursor: 'pointer' },
  reject: { border: 0, borderRadius: '6px', padding: '10px 16px', background: '#b91c1c', color: '#fff', cursor: 'pointer' },
  message: { padding: '30px', textAlign: 'center', color: '#64748b' },
  error: { marginBottom: '16px', padding: '12px', borderRadius: '6px', background: '#fee2e2', color: '#991b1b' }
};

export default AdminPlanRequestManager;
